// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { MedplumClient } from '@medplum/core';
import type { Patient } from '@medplum/fhirtypes';
import {
  appointmentChargeDefinitionUrl,
  PRE_ENCOUNTER_CHARGEABLE_CPTS,
  resolvePayerClass,
  resolvePrice,
} from '@polarissleep/shared';
import type { AppointmentTypeDefinition } from './appointment-types';

export interface ChargeSpec {
  cptCode: string;
  chargeItemDefinitionUrl: string;
  amountUsd: number;
}

/**
 * Resolve the upfront charge (if any) required before an appointment of the given type can be booked.
 *
 * @returns ChargeSpec when the type's CPT is in PRE_ENCOUNTER_CHARGEABLE_CPTS and a priced
 *          ChargeItemDefinition is seeded; null when the CPT is not chargeable.
 * @throws Error when CPT is chargeable but no priced ChargeItemDefinition is seeded —
 *         booking must fail loudly rather than silently skip the charge.
 */
export async function resolveChargeForAppointmentType(
  medplum: MedplumClient,
  type: AppointmentTypeDefinition,
  patient: Patient,
  dateOfService: string
): Promise<ChargeSpec | null> {
  const cpt = type.billingCode;
  if (!cpt || !PRE_ENCOUNTER_CHARGEABLE_CPTS.has(cpt)) {
    return null;
  }

  const url = appointmentChargeDefinitionUrl(cpt);
  const candidates = await medplum.searchResources('ChargeItemDefinition', `url=${url}`);
  const definition = candidates.find((d) => d.url === url);
  const amount = definition ? resolvePrice(definition, resolvePayerClass(patient, dateOfService)) : undefined;

  if (!definition || typeof amount !== 'number' || amount <= 0) {
    throw new Error(`Pricing not configured for ${cpt}`);
  }

  return { cptCode: cpt, chargeItemDefinitionUrl: url, amountUsd: amount };
}
